import {useState} from "react";
import {MapPin, Pencil, Truck, X} from "lucide-react";
import {apiPath, type AdminOrderDetail} from "@zaaaz/shared";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {OrderField, OrderFieldGrid, OrderSection} from "./OrderSection";

type DeliveryForm = {
  awb: string;
  cost: string;
  adresa: string;
  adresa_localitate: string;
  adresa_codpostal: string;
  curier: number;
};

type Props = {
  detail: AdminOrderDetail;
  deliveryForm: DeliveryForm;
  setDeliveryForm: (v: DeliveryForm) => void;
  onSaveDelivery: (id: number) => void;
  onGenAwbSameday: (id: number) => void;
};

export function OrderDeliveryTab({detail, deliveryForm, setDeliveryForm, onSaveDelivery, onGenAwbSameday}: Props) {
  const [editing, setEditing] = useState(false);
  const canEdit = detail.status !== 4 && detail.delivery.curier !== 70;
  const addressLine = [detail.delivery.adresa, detail.delivery.localitate, detail.delivery.judetNume, detail.delivery.codPostal]
    .filter(Boolean)
    .join(", ");

  function cancelEdit() {
    setDeliveryForm({
      awb: detail.delivery.awb || "",
      cost: String(detail.delivery.shippingCost ?? 0),
      adresa: detail.delivery.adresa || "",
      adresa_localitate: detail.delivery.localitate || "",
      adresa_codpostal: detail.delivery.codPostal || "",
      curier: detail.delivery.curier || 50
    });
    setEditing(false);
  }

  async function save() {
    await onSaveDelivery(detail.id);
    setEditing(false);
  }

  return (
    <div className="space-y-4">
      {!editing ? (
        <OrderSection
          title={detail.delivery.curierLabel || "Courier"}
          icon={<Truck />}
          action={canEdit ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 gap-1.5"
              onClick={() => setEditing(true)}
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </Button>
          ) : null}
        >
          <div className="flex gap-3">
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
              <MapPin className="h-4 w-4" />
            </span>
            <div className="min-w-0 space-y-3">
              <p className="text-sm font-medium leading-relaxed">{addressLine || "No address"}</p>
              <OrderFieldGrid>
                {detail.delivery.locatie ? (
                  <OrderField label="Warehouse">{detail.delivery.locatie}</OrderField>
                ) : null}
                <OrderField label="Shipping cost">{(detail.delivery.shippingCost ?? 0).toFixed(2)} lei</OrderField>
                <OrderField label="AWB" span={detail.delivery.locatie ? 1 : 2}>
                  {detail.delivery.awb ? (
                    detail.delivery.awbTrackingUrl ? (
                      <a href={detail.delivery.awbTrackingUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                        {detail.delivery.awb}
                      </a>
                    ) : (
                      detail.delivery.awb
                    )
                  ) : (
                    <span className="font-normal text-muted-foreground">No AWB yet</span>
                  )}
                </OrderField>
              </OrderFieldGrid>
            </div>
          </div>
        </OrderSection>
      ) : (
        <OrderSection
          title="Edit delivery"
          icon={<Truck />}
          action={
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={cancelEdit} aria-label="Cancel">
              <X className="h-4 w-4" />
            </Button>
          }
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <Input placeholder="AWB" value={deliveryForm.awb} onChange={(e) => setDeliveryForm({...deliveryForm, awb: e.target.value})} />
            <Input placeholder="Shipping cost" value={deliveryForm.cost} onChange={(e) => setDeliveryForm({...deliveryForm, cost: e.target.value})} />
            <Input placeholder="Address" value={deliveryForm.adresa} onChange={(e) => setDeliveryForm({...deliveryForm, adresa: e.target.value})} className="sm:col-span-2" />
            <Input placeholder="City" value={deliveryForm.adresa_localitate} onChange={(e) => setDeliveryForm({...deliveryForm, adresa_localitate: e.target.value})} />
            <Input placeholder="Postal code" value={deliveryForm.adresa_codpostal} onChange={(e) => setDeliveryForm({...deliveryForm, adresa_codpostal: e.target.value})} />
            <div className="flex gap-2 sm:col-span-2">
              <Button type="button" onClick={save}>Save delivery</Button>
              <Button type="button" variant="outline" onClick={cancelEdit}>Cancel</Button>
            </div>
          </div>
        </OrderSection>
      )}

      {(detail.delivery.awbGlsApiPath || detail.delivery.awbSamedayApiPath) ? (
        <OrderSection title="AWB actions" dense>
          <div className="flex flex-wrap gap-2">
            {detail.delivery.awbGlsApiPath ? (
              <Button variant="outline" size="sm" asChild>
                <a href={apiPath(detail.delivery.awbGlsApiPath)} target="_blank" rel="noreferrer">AWB GLS</a>
              </Button>
            ) : null}
            {detail.delivery.awbSamedayApiPath ? (
              <Button type="button" variant="outline" size="sm" onClick={() => onGenAwbSameday(detail.id)}>
                AWB SameDay
              </Button>
            ) : null}
          </div>
        </OrderSection>
      ) : null}
    </div>
  );
}
